import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import {
    SplitOrderInvoicesDataItem,
    SplitOrderInvoicesListAction,
    SplitOrderInvoicesListStoreHook,
} from '../split_order_invoices_list_types';
import { splitOrderInvoicesDataAtom, splitOrderInvoicesPageStateAtom } from '../split_order_invoices_list_atoms';
import { useCallback } from 'react';
import { tableDataVM } from '../../../../core/table/table_view_models';
import { SplitOrderInvoicesSearchModel } from '../search/split_order_invoices_search_types';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import utilSvc from '../../../../core/utilSvc';
import { getDefaultStore } from 'jotai';
import { splitOrderInvoicesSearchFormAtom } from '../search/split_order_invoices_search_atoms';
import splitOrderInvoicesListApiToVM from '../table/splitOrderInvoicesListApiToVM';
import splitOrderInvoicesRepo from '../../data/splitOrderInvoicesRepo';
import { useRouter } from '@uirouter/react';
import { SPLIT_ORDER_INVOICES_ROUTE_DETAIL } from '../../split_order_invoices_const';
import { splitOrderInvoicesDetailPageStateAtom } from '../../detail/split_order_invoices_detail_atoms';
import {
    splitOrderInvoicesListColumnSpec,
    splitOrderInvoicesListViewByCe,
} from '../table/SplitOrderInvoicesListTableSpec';
import { tableStateAtom, tableViewAtom } from '../split_order_invoices_list_atoms';
import { tblSpecLoadView } from '../../../../core/table/tableViewsRepo';

type SplitOrderInvoicesStoreActions = Pick<
    SplitOrderInvoicesListStoreHook,
    'getList' | 'setAction' | 'showHelp' | 'navigateToDetailsPage'
>;

const useSplitOrderInvoicesStoreActionsCore = (): SplitOrderInvoicesStoreActions => {
    const setState = useSetImmerAtom(splitOrderInvoicesPageStateAtom);
    const setList = useSetImmerAtom(splitOrderInvoicesDataAtom);
    const setDetailsPageState = useSetImmerAtom(splitOrderInvoicesDetailPageStateAtom);
    const setTableState = useSetImmerAtom(tableStateAtom);
    const setView = useSetImmerAtom(tableViewAtom);
    const router = useRouter();

    const getListAsync = useCallback(
        async (query: SplitOrderInvoicesSearchModel) => {
            try {
                ngDelegate.wait(true, true);
                setState(draft => {
                    draft.loading = true;
                    draft.action = 'QUERY';
                    return draft;
                });
                const res = await splitOrderInvoicesRepo.getList(query);
                const cleanedRes = splitOrderInvoicesListApiToVM(res);
                setState(draft => {
                    draft.action = 'LIST';
                    draft.loading = false;
                    return draft;
                });
                setList(() => tableDataVM<SplitOrderInvoicesDataItem>(cleanedRes));

                // Toggle CEID column visibility based on CE selection
                const showCeid = query.CE?.val === 'ALL';
                const colSpec = splitOrderInvoicesListColumnSpec();
                const ceView = splitOrderInvoicesListViewByCe(showCeid);
                setTableState(draft => {
                    draft.currentViewId = ceView.key;
                });
                setView(
                    () =>
                        tblSpecLoadView(ceView.key, [ceView], colSpec) ?? {
                            cols: [],
                            sections: [],
                            sort: { col: '', dir: '' },
                        },
                );

                ngDelegate.wait(false);
            } catch (err: any) {
                setList(() => tableDataVM<SplitOrderInvoicesDataItem>());
                setState(draft => {
                    draft.loading = false;
                    return draft;
                });

                // apiSvc already raises user-facing warnings for handled HTTP cases.
                // Only show fallback warning when an unexpected error bubbles up.
                if (!utilSvc.http.isIgnore(err)) {
                    ngDelegate.warn(
                        'Split invoice search failed',
                        'useSplitOrderInvoicesStore.getListAsync',
                        String(err?.status || ''),
                        utilSvc.http.safeResponse(err),
                    );
                }
                ngDelegate.wait(false);
            }
        },
        [setList, setState, setTableState, setView],
    );

    const getList = useCallback(
        (query?: SplitOrderInvoicesSearchModel): void => {
            if (query) {
                getListAsync(query);
                return;
            }
            const defaultStore = getDefaultStore();
            const searchForm = defaultStore.get<SplitOrderInvoicesSearchModel>(splitOrderInvoicesSearchFormAtom);
            getListAsync(searchForm);
        },
        [getListAsync],
    );

    const setAction = useCallback(
        (action: SplitOrderInvoicesListAction): void => {
            setState(draft => {
                draft.action = action;
                return draft;
            });
        },
        [setState],
    );

    const showHelp = useCallback((): void => {
        ngDelegate.helpLink('order-splitinvoices', 'Split Invoices');
    }, []);

    const navigateToDetailsPage = useCallback(
        (rowItem: SplitOrderInvoicesDataItem): void => {
            const defaultStore = getDefaultStore();
            const searchForm = defaultStore.get<SplitOrderInvoicesSearchModel>(splitOrderInvoicesSearchFormAtom);
            setDetailsPageState(draft => {
                draft.id = rowItem.Id;
                draft.title = 'Split Invoice Detail';
                draft.rebatesOnly = searchForm.RebatesOnly || false;
            });
            router.stateService.go(SPLIT_ORDER_INVOICES_ROUTE_DETAIL, { Id: rowItem.Id });
        },
        [router.stateService, setDetailsPageState],
    );

    return { getList, setAction, showHelp, navigateToDetailsPage };
};

export const useSplitOrderInvoicesStoreActions = (): SplitOrderInvoicesStoreActions => {
    return useSplitOrderInvoicesStoreActionsCore();
};

export const useSplitOrderInvoicesStore = (): SplitOrderInvoicesListStoreHook => {
    const [state] = useImmerAtom(splitOrderInvoicesPageStateAtom);
    const { getList, setAction, showHelp, navigateToDetailsPage } = useSplitOrderInvoicesStoreActionsCore();
    return { action: state.action, loading: state.loading, getList, setAction, showHelp, navigateToDetailsPage };
};
